import { ITicketOption } from "../../Orchestrator/Types/Interface/ITicketOption";
import { ServiceClassTypeEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";

/**
 * Minimum shape of a product needed to resolve its meta ticket option.
 * Used in place of the full `IPassOfferProduct` / `ITripProduct` so this
 * helper can be called from both pass-offer and booked-trip contexts
 * without an `as any` cast — both interfaces structurally provide
 * `productTags` and `serviceClass`.
 */
export interface ITicketOptionProductRef {
  productTags?: string[] | null;
  serviceClass?: { type?: string | null; name?: string | null } | null;
}

/**
 * Bridge the two tier vocabularies in the codebase:
 *
 *   - Product side (`product.serviceClass.type`) carries
 *     `ServiceClassTypeEnum.HIGH` for premium (default "HIGH", env-overridable
 *     via NEXT_PUBLIC_SERVICE_CLASS_PREMIUM) and `ServiceClassTypeEnum.STANDARD`
 *     for standard (default "STANDARD", env-overridable via
 *     NEXT_PUBLIC_SERVICE_CLASS_STANDARD).
 *   - CMS meta side (`ITicketOption.serviceClass`) carries the literals
 *     "PREMIUM" / "STANDARD" (matched verbatim by every other consumer — see
 *     `passSummary?.summary.ticket?.serviceClass === "PREMIUM"` in
 *     PriceDetails / OfferPass*Section / BookedPass / BookedOfferPassDetail).
 *
 * The default envs happen to align for standard ("STANDARD" === "STANDARD")
 * but diverge for premium ("HIGH" !== "PREMIUM"). We translate both tiers
 * defensively so a future ops change to either env (`SERVICE_CLASS_PREMIUM=X`
 * or `SERVICE_CLASS_STANDARD=Y`) doesn't silently break passes — we compare
 * against the enum values (post-toLowerCase) rather than literal "high" /
 * "standard".
 */
const productTierToMetaTier = (productTier: string): string => {
  if (productTier === ServiceClassTypeEnum.HIGH.toLowerCase()) return "premium";
  if (productTier === ServiceClassTypeEnum.STANDARD.toLowerCase()) return "standard";
  return productTier;
};

/**
 * Resolve the meta `ITicketOption` record (the SFC/SFF/STV/PFP/...
 * canonical entry that drives the class & ticket options dropdown and
 * receipt) for a given pass / booked-offer product.
 *
 * Matches on (a) one of the product's `productTags` against the option's
 * `label` (case- and whitespace-insensitive) AND (b) the product's
 * `serviceClass.type` against the option's `serviceClass`. The service
 * class guard disambiguates tier-name collisions across STANDARD /
 * PREMIUM (e.g. a hypothetical "Saver" label in both tiers).
 *
 * Returns `null` when the product has no recognisable tag, when the meta
 * doesn't carry a matching option, or when the inputs are missing.
 * Callers should fall back gracefully (empty dropdown / no chip / no
 * detail) rather than fabricating a default — the legacy
 * `STANDARD_OFFER_PASS_PRODUCT_CODES` lists silently bit-rotted whenever
 * the backend shipped a new code (e.g. `"3DAYS"`), and pretending we
 * recognise unknown tiers is what we're moving away from.
 */
export const findTicketOptionForProduct = (
  product: ITicketOptionProductRef | null | undefined,
  ticketOptions: ITicketOption[] | null | undefined,
): ITicketOption | null => {
  if (!product || !ticketOptions?.length) return null;
  const tags = (product.productTags || []).filter(Boolean);
  const norm = (s: string | undefined | null) => (s || "").trim().toLowerCase();
  // Translate the product-side tier ("HIGH") into the meta vocabulary
  // ("PREMIUM") before comparison — see productTierToMetaTier above.
  const productTier = productTierToMetaTier(norm(product.serviceClass?.type));

  // Service-class match is the hard requirement — same tier label can
  // exist on multiple tiers and we never want to cross-match.
  const sameTier = ticketOptions.filter(
    (opt) => norm(opt.serviceClass) === productTier,
  );
  if (!sameTier.length) return null;

  const normalizedTags = tags.map(norm);

  // Prefer an exact label match. Falls back to substring match so a
  // looser tag (`"Standard Flex"`) still matches a more specific meta
  // label (`"Standard Flex Plus"`), and vice versa. Each level resolves
  // deterministically before moving to the next, so an exact match
  // always wins over a substring match even if both exist.
  const exact = sameTier.find((opt) =>
    normalizedTags.some((tag) => tag === norm(opt.label)),
  );
  if (exact) return exact;

  const substring = sameTier.find((opt) => {
    const optLabel = norm(opt.label);
    if (!optLabel) return false;
    return normalizedTags.some(
      (tag) => tag && (optLabel.includes(tag) || tag.includes(optLabel)),
    );
  });
  return substring || null;
};
